import { IconFilter, IconSettings } from '@tabler/icons-react';
import classNames from 'classnames';
import { memo } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import { FilterItem } from '../../utils/types';
import * as styles from '../../styles.module.scss';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../utils/constants';

interface TableControlsProps {
  tableId: string;
  enablePagination: boolean;
  enableFilter: boolean;
  enableManageColumn: boolean;
  enableServerSidePagination: boolean;
  appliedFilters: FilterItem[];
  isColumnModified: boolean;
  autoRefreshEnabled: boolean;
  countdown: number;
  initCountdown: number;
  setInitCountdown: (value: number) => void;
  onClearFilters: () => void;
  onRowsPerPageChange: (value: number) => void;
  onAutoRefreshToggle: () => void;
  rowsPerPage: number;
}

const TableControls = memo(
  ({
    tableId,
    enablePagination,
    enableFilter,
    enableManageColumn,
    enableServerSidePagination,
    appliedFilters,
    isColumnModified,
    autoRefreshEnabled,
    countdown,
    initCountdown,
    setInitCountdown,
    onClearFilters,
    onRowsPerPageChange,
    onAutoRefreshToggle,
    rowsPerPage,
  }: TableControlsProps) => {
    const { t } = useTranslation();

    const handleRowsPerPageChange = (
      e: React.ChangeEvent<HTMLSelectElement>,
    ) => {
      const value = parseInt(e.target.value);
      if (!isNaN(value)) {
        onRowsPerPageChange(value);
      }
    };

    return (
      <div className="card-body border-bottom py-3 d-flex justify-content-between">
        {enablePagination && (
          <div className={classNames('text-secondary', styles.showEntries)}>
            {t('SHOW')}
            <div className="mx-2 d-inline-block">
              <select
                className="form-select form-select-sm"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                style={{ width: '64px' }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            {t('ENTRIES')}
          </div>
        )}

        <div className="d-flex justify-content-end">
          {enableFilter && (
            <div className="d-flex g-1 align-items-center">
              {appliedFilters.length > 0 && (
                <a
                  href="#"
                  className="text-sm me-2 d-none d-md-inline-block"
                  onClick={onClearFilters}
                >
                  {t('CLEAR_FILTERS')}
                </a>
              )}
              <span
                data-bs-toggle="offcanvas"
                data-bs-target={`#offcanvasFilter-${tableId}`}
                className={classNames(
                  'me-2 cursor-pointer form-selectgroup-label',
                  styles.btnFunction,
                  appliedFilters.length > 0 && styles.active,
                )}
              >
                <IconFilter width={20} />
              </span>
            </div>
          )}

          {enableManageColumn && (
            <span
              data-bs-toggle="modal"
              data-bs-target="#manageColumnModal"
              className={classNames(
                'cursor-pointer form-selectgroup-label',
                styles.btnFunction,
                isColumnModified && styles.active,
              )}
            >
              <IconSettings size={24} />
            </span>
          )}

          {enableServerSidePagination && (
            <div
              className={classNames(
                'ms-2 cursor-pointer form-selectgroup-label dropdown',
                styles.btnFunction,
                autoRefreshEnabled && styles.active,
              )}
              style={{ lineHeight: '23px' }}
            >
              <span
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                className="dropdown-btn"
                onClick={(e) => {
                  if (autoRefreshEnabled) {
                    e.preventDefault();
                    onAutoRefreshToggle();
                  }
                }}
              >
                {t('AUTO_REFRESH')} {autoRefreshEnabled && `(${countdown})`}
              </span>
              <ul className="dropdown-menu mt-3">
                <li>
                  <h6 className="dropdown-header">{t('SECONDS_TO_REFRESH')}</h6>
                </li>
                <div className="p-2">
                  <div className="mt-2 d-flex justify-content-center">
                    <input
                      type="text"
                      className="form-control me-2"
                      value={initCountdown}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value > 0) {
                          setInitCountdown(value);
                        }
                      }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={onAutoRefreshToggle}
                    >
                      {t('APPLY')}
                    </button>
                  </div>
                </div>
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  },
);

TableControls.displayName = 'TableControls';

export default TableControls;
